import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAuthContext = createContext(null);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error("useAdminAuth must be used within AdminAuthProvider");
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("admin_token"));

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const storedToken = localStorage.getItem("admin_token");
    if (!storedToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` },
        withCredentials: true
      });
      setAdmin(response.data);
      setToken(storedToken);
    } catch (error) {
      localStorage.removeItem("admin_token");
      setAdmin(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(
      `${API_URL}/api/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    const newToken = response.data.token;
    localStorage.setItem("admin_token", newToken);
    setToken(newToken);
    setAdmin(response.data.user);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      // Ignore logout errors
    }
    localStorage.removeItem("admin_token");
    setAdmin(null);
    setToken(null);
  };

  const changePassword = async (currentPassword, newPassword) => {
    const response = await axios.post(
      `${API_URL}/api/auth/change-password`,
      { current_password: currentPassword, new_password: newPassword },
      { 
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true 
      }
    );
    
    // Update admin state to reflect password change complete
    if (admin) {
      setAdmin({ ...admin, must_change_password: false });
    }
    
    return response.data;
  };

  const refreshAuth = async () => {
    await checkAuth();
  };

  return (
    <AdminAuthContext.Provider value={{ 
      admin, 
      loading, 
      token, 
      login, 
      logout, 
      changePassword,
      refreshAuth,
      checkAuth 
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
};
